import { useMemo } from 'react';
import { useCanSeeCrowdVoltConnect } from '../access/useFeatureAccess';
import { useEntitlement } from '../hooks/useApi';
import { FlashtoolsEntitlement } from '../types';
import { useV2Translation } from './i18n';
import { SHOW_LOCKED_NAV_ITEMS } from './nav';
import {
  NavSectionId,
  SECTION_LABEL_KEYS,
  SECTION_ORDER,
  V2_ROUTES,
  V2RouteDef,
} from './routes';
import { NavIconName } from './components/icons';
import { useAccountAttentionCount } from './components/AccountHealthBanners';
import { useSeatSession } from './useSeatSession';

/**
 * Turns the route table into the nav model the sidebar and the command palette
 * both render. One source of truth, so the two can never disagree about what a
 * user can see.
 */

export interface NavItemView {
  id: string;
  path: string;
  label: string;
  icon: NavIconName;
  /** Rendered dimmed with a padlock and a tier chip; still a real link. */
  locked: boolean;
  /** `Pro` / `Reserve` — only set when `locked`. */
  tierChip?: string;
  /** `title` attribute, e.g. `Waitlists is included in Pro and Reserve`. */
  title?: string;
  /** Amber attention dot + count (Connections). */
  attention?: number;
  /** "Beta" chip beside the label. */
  beta?: boolean;
}

export interface NavSectionView {
  id: NavSectionId;
  label: string;
  items: NavItemView[];
}

/** v1's `canSeeNavItem`, unchanged. */
function canSee(route: V2RouteDef, ent: FlashtoolsEntitlement | undefined): boolean {
  if (!route.gate) return true;
  if (!ent) return false;
  if (ent.comped) return true;
  return ent.features.includes(route.gate.feature);
}

/**
 * Whether an account may so much as learn that Flash Buy exists. Same rule the
 * sidebar applies to the invite-only Drops item: entitled or comped accounts
 * see it, accounts holding a Reserve invite see it as the nudge to subscribe,
 * everyone else must not find it anywhere — not in the nav, not in the Support
 * form's category list, not as a usage row on Profile.
 */
export function canSeeBuyingTools(ent: FlashtoolsEntitlement | undefined): boolean {
  if (!ent) return false;
  return ent.comped || ent.reserve_invited || ent.features.includes('buying_tools');
}

const TIER_LABELS: Record<string, string> = {
  core: 'Core',
  pro: 'Pro',
  reserve: 'Reserve',
};

export function useV2Nav(): {
  sections: NavSectionView[];
  /** Chip beside `Profile` in the footer zone. */
  planLabel: string | null;
} {
  const { t } = useV2Translation();
  const { data: entitlement } = useEntitlement();
  const attention = useAccountAttentionCount();
  const showStagedSurfaces = useCanSeeCrowdVoltConnect();
  const { isSeat, isLoading: seatLoading } = useSeatSession();

  return useMemo(() => {
    const sections = SECTION_ORDER.map((sectionId) => {
      const items: NavItemView[] = [];

      V2_ROUTES.filter((r) => r.section === sectionId).forEach((route) => {
        if (route.staged && !showStagedSurfaces) return; // CrowdVolt staging surface
        // A team member sees only the surfaces marked `seatVisible`. Hidden, not
        // locked: a padlock invites a click that would 403, and there is
        // nothing the team member could do about it — it is not their account to
        // upgrade. While the answer is still loading, show a team member's nav
        // rather than an owner's; briefly hiding Billing from an owner is a
        // far smaller error than briefly offering it to a team member.
        if ((isSeat || seatLoading) && !route.seatVisible) return;  // see seatVisibleRoutes()
        const visible = canSee(route, entitlement);
        // Invite-only routes stay undiscoverable when locked — except for
        // accounts that hold an invite, who see the locked item as the nudge
        // to subscribe.
        const hideLocked =
          route.hideWhenLocked && !entitlement?.reserve_invited;
        if (!visible && (!SHOW_LOCKED_NAV_ITEMS || hideLocked)) return;

        const locked = !visible;
        items.push({
          id: route.id,
          path: route.path,
          label: t(route.labelKey),
          icon: route.icon,
          locked,
          tierChip: locked ? route.gate?.tierChip : undefined,
          title: locked && route.gate
            ? `${t(route.labelKey)} is included in ${route.gate.requiredTier.replace(' or ', ' and ')}`
            : undefined,
          attention: route.id === 'accounts' && attention > 0 ? attention : undefined,
          beta: route.beta || undefined,
        });
      });

      // Locked items sort last within their section so reachable items stay at
      // consistent positions. For a Reserve user nothing is locked, so nothing
      // moves.
      items.sort((a, b) => Number(a.locked) - Number(b.locked));

      return { id: sectionId, label: t(SECTION_LABEL_KEYS[sectionId]), items };
    }).filter((section) => section.items.length > 0);

    const planLabel = entitlement?.tier
      ? TIER_LABELS[entitlement.tier] ?? entitlement.tier
      : entitlement?.comped
        ? 'Admin'
        : null;

    return { sections, planLabel };
  }, [t, entitlement, attention, showStagedSurfaces, isSeat, seatLoading]);
}
